// FormTemplateService — form templates + assigned forms. FULLY migrated to the OpenAPI-generated
// client (AdminFormTemplateController + AdminFormController), including the paged LIST endpoints:
// the server now models their pagination as FLAT page/size/sort query params (springdoc
// @ParameterObject, server PR #434), so the SDK serializes them in a form Spring binds. The server
// derives the company from the caller's principal (IDOR sweep) — the endpoints have no companyId
// param — so the legacy `companyId` argument is accepted for signature compatibility but NOT forwarded.
//
// Migrated functions return `{data}` (callers read res.data) and re-throw an axios-shaped error.

import {
    adminFormTemplateControllerGetAll,
    adminFormTemplateControllerGetFormsByTemplate,
    adminFormTemplateControllerGetTraineesWithFormStatus,
    adminFormControllerGetByTrainee,
    adminFormTemplateControllerGetById,
    adminFormTemplateControllerCreate,
    adminFormTemplateControllerDelete,
    adminFormTemplateControllerAssignToTrainee,
    adminFormTemplateControllerCanAssignToTrainee,
    adminFormControllerDelete,
    adminFormControllerGetById,
    adminFormControllerSaveAnswers,
    adminFormControllerGenerateAiSummary,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
    throw {response: {data: error, status: response?.status}};
};

/**
 * Get form templates with pagination
 * @returns {Promise} - Paginated form templates
 *
 * Migrated to the OpenAPI client (GET /admin/form-template). The server now models paging as FLAT
 * page/size/sort query params (springdoc @ParameterObject). Returns {data} = the page body (callers
 * read res.data.content). `companyId` is no longer sent (dropped server-side in the company-scoping
 * sweep; the server derives company from the authed principal) — the arg is kept for signature parity.
 */
export const getFormTemplates = async (page = 0, size = 20, companyId = null, sort = []) => { // eslint-disable-line no-unused-vars
    const {data, error, response} = await adminFormTemplateControllerGetAll({
        query: {
            page,
            size,
            ...(sort.length > 0 ? {sort} : {}),
        },
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Get a single form template by ID
 * @returns {Promise} - Form template (caller reads response.data)
 */
export const getFormTemplate = async (templateId, companyId = null) => { // eslint-disable-line no-unused-vars
    const {data, error, response} = await adminFormTemplateControllerGetById({path: {id: templateId}});
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Create a new form template
 * @returns {Promise} - Created template
 */
export const createFormTemplate = async (templateData, companyId = null) => { // eslint-disable-line no-unused-vars
    const {data, error, response} = await adminFormTemplateControllerCreate({body: templateData});
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Delete a form template
 * @returns {Promise} - Delete result
 */
export const deleteFormTemplate = async (templateId, companyId = null) => { // eslint-disable-line no-unused-vars
    const {data, error, response} = await adminFormTemplateControllerDelete({path: {id: templateId}});
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Assign a template to a trainee (creates a form from template)
 * @returns {Promise} - Assignment result
 */
export const assignTemplateToTrainee = async (templateId, traineeId, name = null, required = false) => {
    const query = {required};
    if (name) query.name = name;
    const {data, error, response} = await adminFormTemplateControllerAssignToTrainee({
        path: {templateId, traineeId},
        query,
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Get forms created from a specific template
 * @returns {Promise} - Paginated forms list
 *
 * Migrated to the OpenAPI client (GET /admin/form-template/{templateId}/forms, flat page/size/sort).
 * Returns {data} = the page body (callers read res.data.content).
 */
export const getFormsByTemplate = async (templateId, page = 0, size = 20, sort = []) => {
    const {data, error, response} = await adminFormTemplateControllerGetFormsByTemplate({
        path: {templateId},
        query: {
            page,
            size,
            ...(sort.length > 0 ? {sort} : {}),
        },
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Check if a template can be assigned to a trainee
 * @returns {Promise} - Boolean result
 */
export const canAssignTemplateToTrainee = async (templateId, traineeId) => {
    const {data, error, response} = await adminFormTemplateControllerCanAssignToTrainee({
        path: {templateId, traineeId},
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Get trainees with their form status for a specific template
 * @returns {Promise} - Paginated list of trainees with form status
 *
 * Migrated to the OpenAPI client (GET /admin/form-template/{templateId}/trainees, flat page/size).
 * Returns {data} = the page body (callers read res.data.content). `companyId` is no longer sent
 * (dropped server-side in the company-scoping sweep); the arg is kept for signature parity.
 */
export const getTraineesWithFormStatus = async (templateId, companyId = null, page = 0, size = 1000) => { // eslint-disable-line no-unused-vars
    const {data, error, response} = await adminFormTemplateControllerGetTraineesWithFormStatus({
        path: {templateId},
        query: {page, size},
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Get forms assigned to a specific trainee
 * @returns {Promise} - Paginated forms list
 *
 * Migrated to the OpenAPI client (GET /admin/form/trainee/{traineeId}, flat page/size + status).
 * Returns {data} = the page body (callers read res.data.content).
 */
export const getTraineeForms = async (traineeId, status = null, page = 0, size = 20) => {
    const {data, error, response} = await adminFormControllerGetByTrainee({
        path: {traineeId},
        query: {
            ...(status !== null ? {status} : {}),
            page,
            size,
        },
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Delete a form assigned to a trainee
 * @returns {Promise} - Delete result
 */
export const deleteTraineeForm = async (formId) => {
    const {data, error, response} = await adminFormControllerDelete({path: {id: formId}});
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Get a specific form by ID (with questions and answers)
 * @returns {Promise} - Form with questions and answers (caller reads response.data)
 */
export const getFormById = async (formId) => {
    const {data, error, response} = await adminFormControllerGetById({path: {id: formId}});
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Save answers for a form
 * @param {number} formId
 * @param {object[]} answers
 * @param {'SUBMIT'|'PARTIAL'} submitType
 * @returns {Promise} - Updated form
 */
export const saveFormAnswers = async (formId, answers, submitType = 'SUBMIT') => {
    const {data, error, response} = await adminFormControllerSaveAnswers({
        path: {id: formId},
        query: {submitType},
        body: {answers},
    });
    if (error) throwLikeAxios(error, response);
    return {data};
};

/**
 * Generate AI summary for a completed form
 * @returns {Promise} - Updated form with AI summary
 */
export const generateAISummary = async (formId) => {
    const {data, error, response} = await adminFormControllerGenerateAiSummary({path: {id: formId}});
    if (error) throwLikeAxios(error, response);
    return {data};
};
